import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import "../CSS/login.css";
import logo from "../images/logo.png";
import { BASE_API_URL } from "../constants";

// ── BTS Images: place bts1.jpg … bts35.jpg inside public/bts/ ────────────────
const BTS_IMAGES = Array.from({ length: 35 }, (_, i) => `/bts/bts-${i + 1}.jpg`);
// ─────────────────────────────────────────────────────────────────────────────

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  // ── Full-page BTS background cycling ─────────────────────────────────────
  const [bgIndex, setBgIndex] = useState(0);
  const [bgVisible, setBgVisible] = useState(true); // controls opacity for fade

  useEffect(() => {
    const interval = setInterval(() => {
      // Step 1: fade out current image
      setBgVisible(false);

      // Step 2: after fade-out (700ms), swap image and fade back in
      setTimeout(() => {
        setBgIndex((prev) => (prev + 1) % BTS_IMAGES.length);
        setBgVisible(true);
      }, 700);
    }, 5000); // change every 5 seconds

    return () => clearInterval(interval);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  const apiBaseUrl = (BASE_API_URL || "").replace(/\/+$/, "");

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setMessage("");
    setIsSubmitting(true);

    try {
      const loginData = new FormData();
      loginData.append("username", username.trim());
      loginData.append("password", password);

      const response = await axios.post(
        `${apiBaseUrl}/api/contributor-login`,
        loginData
      );

      const userData = response.data?.user;

      if (!userData?.userId) {
        throw new Error("Invalid response format from server.");
      }

      const { userId, email, username: fetchedUsername } = userData;

      sessionStorage.setItem("userId", String(userId));
      sessionStorage.setItem("userName", fetchedUsername || username.trim());
      if (email) sessionStorage.setItem("email", email);

      // Pre-fetch profile photo so it shows instantly on Dashboard
      if (email) {
        try {
          const profileRes = await fetch(
            `${apiBaseUrl}/api/user-profile?email=${encodeURIComponent(email)}`
          );
          if (profileRes.ok) {
            const profileData = await profileRes.json();
            const rawPhoto = profileData?.user?.photoUrl;
            if (rawPhoto && !rawPhoto.includes("dummyimage")) {
              const finalPhoto = rawPhoto.startsWith("http")
                ? rawPhoto
                : `${apiBaseUrl}${rawPhoto}`;
              sessionStorage.setItem("userPhotoUrl", finalPhoto);
            }
            if (profileData?.user) {
              const fullName = `${profileData.user.firstname || ""} ${profileData.user.lastname || ""}`.trim();
              if (fullName) {
                sessionStorage.setItem("userFullName", fullName);
              }
            }
          }
        } catch (profileErr) {
          console.warn("Could not pre-fetch profile photo:", profileErr);
        }
      }

      await Swal.fire({
        title: "Login Successful",
        text: "Welcome back to Cinevestor!",
        icon: "success",
        confirmButtonColor: "#ff8906",
        timer: 1200,
        timerProgressBar: true,
        showConfirmButton: false,
      });

      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Login error:", error);

      const errMsg =
        error.response?.data?.detail ||
        error.message ||
        "Server not responding or invalid credentials";

      Swal.fire({
        title: "Login Failed",
        text: errMsg,
        icon: "error",
        confirmButtonColor: "#ff8906",
      });

      setMessage(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="login-page">

      {/* ── Full-page BTS Background Image (fades in/out on change) ── */}
      <div
        className="page-bg"
        style={{
          backgroundImage: `url(${BTS_IMAGES[bgIndex]})`,
          opacity: bgVisible ? 1 : 0,
        }}
      />
      {/* Dark cinematic overlay sits on top of the background */}
      <div className="page-bg-overlay" />

      {/* ── Left Panel (hidden on mobile) ── */}
      <section className="film-section">
        <div className="film-content">
          <img src={logo} alt="Logo" className="logo-1" />
          <h1 className="film-title">Where Creativity Meets Contributions</h1>
          <p className="film-subtitle">
            The premier platform connecting passionate contributors with groundbreaking cinematic projects. Join our community to bring movies to life.
          </p>

          <div className="features">
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-hand-holding-usd"></i>
              </div>
              <div className="feature-text">
                <h3>For Contributors</h3>
                <p>Discover promising movie projects and track your contributions</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-film"></i>
              </div>
              <div className="feature-text">
                <h3>Movie Projects</h3>
                <p>Explore a wide variety of cinematic projects and be part of their success</p>
              </div>
            </div>
            <div className="feature">
              <div className="feature-icon">
                <i className="fas fa-network-wired"></i>
              </div>
              <div className="feature-text">
                <h3>Global Network</h3>
                <p>Connect with professionals worldwide in the film industry</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Right Panel — Login Form ── */}
      <div className="auth-container">
        <div className="auth-content">
          <div className="auth-header">
            <h2 className="auth-title">Welcome Back</h2>
            <p className="auth-subtitle">Login to your Cinevestor account</p>
          </div>

          <div className="user-type-toggle">
            <div className="user-option investor active">
              <i className="fas fa-hand-holding-usd"></i>
              <div>Contributor</div>
            </div>
          </div>

          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="input-group">
              <label htmlFor="username">Username</label>
              <input
                type="text"
                id="username"
                name="username"
                placeholder="Enter your Username"
                required
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
              <i className="fas fa-shield-alt" style={{ marginTop: "13px" }}></i>
            </div>

            <div className="input-group">
              <label htmlFor="password">Password</label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                placeholder="Enter your password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <i
                className={`fas ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
                onClick={() => setShowPassword(!showPassword)}
              ></i>
            </div>

            <div className="remember-forgot">
              <div className="remember-me">
                <input type="checkbox" id="remember" />
                <label htmlFor="remember">Remember me</label>
              </div>
              <div className="forgot-password">
                <Link to="/forgetPassword">Forgot password?</Link>
              </div>
            </div>

            <button type="submit" className="auth-button" disabled={isSubmitting}>
              {isSubmitting ? "Logging in..." : "Login to Account"}
            </button>

            {message && (
              <p className="error-text" role="alert">
                {message}
              </p>
            )}

            <div className="auth-footer">
              New to Cinevestor?{" "}
              <Link to="/signup" style={{ textDecoration: "none" }}>
                Create an account
              </Link>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Login;
